import { useState } from 'react'
import { FileText } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import type {
  ChargebackFileTypeChoice,
  ChargebackFileTypeSelectionModalProps,
} from '../../types/components'
import {
  CHARGEBACK_DETAIL_INPUT_CLASS,
  CHARGEBACK_DETAIL_LABEL_CLASS,
  CHARGEBACK_DETAIL_PRIMARY_BUTTON_CLASS,
  CHARGEBACK_DETAIL_SECONDARY_BUTTON_CLASS,
  CHARGEBACK_FILE_TYPE_OTHER,
} from '../../constants/chargebackDetailConstants'

const EMPTY_CHOICE: ChargebackFileTypeChoice = { checkType: '', otherValue: '' }

export function ChargebackFileTypeSelectionModal({
  title,
  files,
  fileTypes,
  onCancel,
  onSubmit,
}: ChargebackFileTypeSelectionModalProps) {
  const [choices, setChoices] = useState<ChargebackFileTypeChoice[]>(() =>
    files.map(() => ({ ...EMPTY_CHOICE })),
  )

  const patchChoice = (index: number, patch: Partial<ChargebackFileTypeChoice>) =>
    setChoices((prev) =>
      prev.map((choice, choiceIndex) =>
        choiceIndex === index ? { ...choice, ...patch } : choice,
      ),
    )

  const isValid =
    files.length > 0 &&
    choices.length === files.length &&
    choices.every(
      (choice) =>
        Boolean(choice.checkType) &&
        (choice.checkType !== CHARGEBACK_FILE_TYPE_OTHER ||
          Boolean(choice.otherValue.trim())),
    )

  return (
    <OnScreenModal
      isOpen
      title={title}
      maxWidth="max-w-2xl"
      onClose={onCancel}
      panelDataTestId="chargeback-file-type-modal"
      footer={
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={!isValid}
            onClick={() => onSubmit(choices)}
            className={CHARGEBACK_DETAIL_PRIMARY_BUTTON_CLASS}
            data-testid="chargeback-file-type-modal-upload"
          >
            Upload
          </button>
          <button
            type="button"
            onClick={onCancel}
            className={CHARGEBACK_DETAIL_SECONDARY_BUTTON_CLASS}
          >
            Cancel
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="hidden gap-4 sm:grid sm:grid-cols-[minmax(0,1fr)_15rem]">
          <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Selected File</span>
          <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Select File Type*</span>
        </div>

        {files.map((file, index) => {
          const choice = choices[index] ?? EMPTY_CHOICE
          return (
            <div
              key={`${file.name}-${index}`}
              className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3 sm:grid-cols-[minmax(0,1fr)_15rem] sm:items-start"
            >
              <div className="flex min-w-0 items-center gap-2 sm:h-10">
                <FileText className="h-4 w-4 shrink-0 text-[#1597ff]" aria-hidden />
                <span
                  className="truncate text-sm font-medium text-slate-800"
                  title={file.name}
                >
                  {file.name}
                </span>
              </div>
              <div className="space-y-2">
                <select
                  aria-label={`File type for ${file.name}`}
                  className={CHARGEBACK_DETAIL_INPUT_CLASS}
                  value={choice.checkType}
                  onChange={(event) =>
                    patchChoice(index, { checkType: event.target.value, otherValue: '' })
                  }
                >
                  <option value="">Select Type</option>
                  {Object.entries(fileTypes).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                {choice.checkType === CHARGEBACK_FILE_TYPE_OTHER ? (
                  <input
                    type="text"
                    aria-label={`Custom file type for ${file.name}`}
                    placeholder="Enter File Type"
                    className={CHARGEBACK_DETAIL_INPUT_CLASS}
                    value={choice.otherValue}
                    onChange={(event) =>
                      patchChoice(index, { otherValue: event.target.value })
                    }
                  />
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </OnScreenModal>
  )
}
